import React, { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { submitEditorialInquiry } from '../services/api';

interface Props {
  listingId: number;
  listingName?: string;
  onClose: () => void;
}

const EditorialInquiryForm: React.FC<Props> = ({ listingId, listingName, onClose }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [name, setName]     = useState('');
  const [email, setEmail]   = useState('');
  const [pitch, setPitch]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]   = useState('');
  const [done, setDone]     = useState(false);

  const onSubmit = async () => {
    setError('');
    if (!email) { setError('Please provide an email.'); return; }
    setSubmitting(true);
    try {
      let recaptchaToken: string | undefined;
      if (executeRecaptcha) {
        try { recaptchaToken = await executeRecaptcha('submit_editorial_inquiry'); }
        catch { recaptchaToken = undefined; }
      }
      await submitEditorialInquiry({
        listingId,
        inquirerEmail: email.trim(),
        inquirerName: name.trim() || undefined,
        pitch: pitch.trim() || undefined,
        recaptchaToken,
      });
      setDone(true);
    } catch (e: any) {
      console.error('[EditorialInquiry.submit] fail',
        'msg=', e?.message, 'code=', e?.code, 'name=', e?.name,
        'status=', e?.response?.status, 'data=', JSON.stringify(e?.response?.data),
        'url=', e?.config?.url, 'baseURL=', e?.config?.baseURL,
        'method=', e?.config?.method, 'isAxiosError=', e?.isAxiosError,
        'stack=', e?.stack);
      setError(e?.response?.data?.message ?? 'Could not send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Modal onClose={onClose}>
        <h2>✅ Inquiry sent</h2>
        <p style={{ color: '#666' }}>
          Thanks! The listing owner has been notified. If they accept, you'll get an email with a link to write the editorial.
        </p>
        <button style={btn} onClick={onClose}>Done</button>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginTop: 0 }}>Want to write an editorial?</h2>
      <p style={{ color: '#666', fontSize: 14 }}>
        Pitch an editorial about <strong>{listingName ?? 'this facility'}</strong>. The owner will review and decide.
      </p>

      <label style={lbl}>Your name (optional)</label>
      <input style={inp} value={name} onChange={e => setName(e.target.value)} />

      <label style={lbl}>Your email</label>
      <input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} />

      <label style={lbl}>Your pitch (what would you write?)</label>
      <textarea
        style={{ ...inp, minHeight: 120 }}
        value={pitch}
        onChange={e => setPitch(e.target.value)}
        placeholder="A short paragraph describing the angle, your background, why this listing fits…"
      />

      {error && <p style={{ color: '#c62828', marginTop: 12 }}>{error}</p>}

      <div style={{ marginTop: 20, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button style={btnGhost} onClick={onClose} disabled={submitting}>Cancel</button>
        <button style={btn} onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Sending…' : 'Send inquiry'}
        </button>
      </div>
    </Modal>
  );
};

const Modal: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => (
  <div
    role="dialog"
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    onClick={onClose}
  >
    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 520, width: '100%' }}>
      {children}
    </div>
  </div>
);

const lbl: React.CSSProperties = { display: 'block', marginTop: 14, marginBottom: 4, fontWeight: 600, fontSize: 13 };
const inp: React.CSSProperties = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d4d4d8', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
const btn: React.CSSProperties = { padding: '10px 20px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { padding: '10px 20px', background: 'transparent', color: '#666', border: '1px solid #ccc', borderRadius: 8, cursor: 'pointer' };

export default EditorialInquiryForm;
